import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Progress,
  VStack,
  HStack,
  Image,
  Box,
  useToast
} from '@chakra-ui/react';
import { pxToRem } from "../../utils/editor.ts";
import { FileService } from "../../services/fileService.ts";

interface DoenbaExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  paperTitle?: string;
  paperId?: string;
}

type ExportStatus = 'saving' | 'success' | 'error';

export const DoenbaExportModal: React.FC<DoenbaExportModalProps> = ({ isOpen, onClose, paperTitle = 'Untitled', paperId }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<ExportStatus>('saving');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useToast();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProgress(0);
      setStatus('saving');
      startExport();
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isOpen]);

  const startExport = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    if (!paperId) {
      setStatus('error');
      return;
    }

    // Simulate progress while waiting for API
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          return 90; // Hold at 90% until response comes back
        }
        return prev + 5;
      });
    }, 200);

    try {
      const blob = await FileService.exportPaperToEdi(paperId);
      
      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(100);
      setStatus('success');

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Use paperTitle for filename, ensure it has .edi extension
      const safeTitle = paperTitle.replace(/[^a-zA-Z0-9-_]/g, '_');
      const filename = `${safeTitle}.edi`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Close shortly after success
      setTimeout(() => {
         onClose();
         toast({
          title: "Download Success",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top"
        });
      }, 1000);

    } catch (error) {
      if (timerRef.current) clearInterval(timerRef.current);
      setStatus('error');
      console.error("Export failed:", error);
    }
  };

  const handleCancel = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    onClose();
  };

  const isError = status === 'error';

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent borderRadius={pxToRem(20)} p={pxToRem(10)}>
        <ModalCloseButton color="gray.500" />
        <ModalBody p={pxToRem(30)}>
          <VStack align="stretch" spacing={4}>
            <Text fontSize={pxToRem(24)} fontWeight="bold" fontFamily="Lato">
              Save as .edi File
            </Text>
            <Text fontSize={pxToRem(16)} color="gray.500" fontFamily="Lato" mt={-2}>
              By saving the content in the writing area as a .edi file, you can transfer the file to a different machine and import it into Doenba Edit there.
            </Text>

            <HStack spacing={4} mt={4} align="center">
              <Box
                border="1px solid #E2E8F0"
                borderRadius="md"
                p={2}
                w={pxToRem(60)}
                h={pxToRem(70)}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                 <Image src="/static/doenba-ico.png" alt=".edi File" w="80%" />
              </Box>
              
              <VStack align="start" flex={1} spacing={1}>
                <Text fontWeight="bold" fontSize={pxToRem(18)} fontFamily="Lato">
                  Saving “{paperTitle}. as .edi File”
                </Text>
                
                <Progress 
                    value={progress} 
                    size="sm" 
                    w="100%" 
                    borderRadius="full"
                    colorScheme={isError ? "red" : "blue"}
                    bg="#E2E8F0"
                />
                
                <HStack justify="space-between" w="100%">
                    {isError ? (
                         <Text color="red.500" fontSize="sm" mx="auto">Export Failed</Text>
                    ) : (
                         <Text color="gray.500" fontSize="sm" mx="auto">{progress}% Completed</Text>
                    )}
                </HStack>
              </VStack>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
